import {
  Badge, Button, Card, DataTable, ErrorState, Field, LoadingState, Notice, PageHeader, PhoneInput, Row, Stack,
  normalisePhone, useApi, useI18n, type IdentityResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

/**
 * What the platform team does for someone no single nursery may decide for (Q25, Q27): the resets waiting for
 * it, and, for an account found by its number, moving it to a new number, releasing the number, or handing over a
 * one-time recovery link.
 */
export function Accounts() {
  const { t } = useI18n();

  return (
    <Stack>
      <PageHeader title={t('accounts.title')} lede={t('accounts.lede')} />
      <RecoveryQueue />
      <FindAccount />
    </Stack>
  );
}

function RecoveryQueue() {
  const api = useApi();
  const { t, formatPhone, formatDateTime } = useI18n();
  const queryClient = useQueryClient();

  const requests = useQuery({ queryKey: ['platform', 'recovery-requests'], queryFn: () => api.platformRecovery.list() });

  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: number; approve: boolean }) =>
      approve ? api.platformRecovery.approve(id) : api.platformRecovery.reject(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platform', 'recovery-requests'] }),
  });

  return (
    <Card title={t('accounts.queue.title')} hint={t('accounts.queue.hint')} flush>
      {decide.isError && <ErrorState error={decide.error} />}
      {requests.isPending && <LoadingState />}
      {requests.isError && <ErrorState error={requests.error} onRetry={() => void requests.refetch()} />}
      {requests.data?.items.length === 0 && <p className="dg-card__hint">{t('accounts.queue.empty')}</p>}
      {requests.data && requests.data.items.length > 0 && (
        <DataTable
          caption={t('accounts.queue.title')}
          rows={requests.data.items}
          rowKey={(request) => request.id}
          columns={[
            { key: 'name', header: t('accounts.name'), render: (r) => r.fullName ?? '—' },
            { key: 'phone', header: t('accounts.phone'), render: (r) => formatPhone(r.phone) },
            { key: 'requested', header: t('accounts.requestedAt'), render: (r) => formatDateTime(r.requestedAt) },
            {
              key: 'from',
              header: t('accounts.from'),
              render: (r) => <span dir="ltr">{[r.requestedFromIp, r.userAgent].filter(Boolean).join(' · ') || '—'}</span>,
            },
            {
              key: 'actions',
              header: '',
              render: (r) => (
                <Row>
                  <Button
                    variant="secondary"
                    busy={decide.isPending && decide.variables.id === r.id && decide.variables.approve}
                    onClick={() => decide.mutate({ id: r.id, approve: true })}
                  >
                    {t('accounts.queue.approve')}
                  </Button>
                  <Button
                    variant="ghost"
                    busy={decide.isPending && decide.variables.id === r.id && !decide.variables.approve}
                    onClick={() => decide.mutate({ id: r.id, approve: false })}
                  >
                    {t('accounts.queue.reject')}
                  </Button>
                </Row>
              ),
            },
          ]}
        />
      )}
    </Card>
  );
}

function FindAccount() {
  const api = useApi();
  const { t } = useI18n();
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const find = useMutation({ mutationFn: (normalised: string) => api.platformRecovery.find(normalised) });

  function submit(event: FormEvent) {
    event.preventDefault();
    const normalised = normalisePhone(phone);
    setPhoneError(normalised ? null : t('auth.phone.required'));
    if (normalised) find.mutate(normalised);
  }

  const identity = find.data?.items[0];

  return (
    <Card title={t('accounts.find.title')}>
      <Stack>
        <form onSubmit={submit} noValidate>
          <Row>
            <Field label={t('accounts.phone')} error={phoneError} required>
              {(props) => <PhoneInput {...props} value={phone} onChange={(event) => setPhone(event.target.value)} />}
            </Field>
            <Button type="submit" variant="secondary" busy={find.isPending}>{t('accounts.find.submit')}</Button>
          </Row>
        </form>
        {find.isError && <ErrorState error={find.error} />}
        {find.isSuccess && !identity && <p className="dg-card__hint">{t('accounts.find.none')}</p>}
        {identity && <IdentityActions key={identity.userId} identity={identity} />}
      </Stack>
    </Card>
  );
}

function IdentityActions({ identity }: { identity: IdentityResponse }) {
  const api = useApi();
  const { t, formatPhone, formatDateTime } = useI18n();
  const [newPhone, setNewPhone] = useState('');
  const [newPhoneError, setNewPhoneError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const renumber = useMutation({ mutationFn: (to: string) => api.platformRecovery.renumber(identity.userId, to) });
  const release = useMutation({ mutationFn: () => api.platformRecovery.release(identity.userId) });
  const link = useMutation({ mutationFn: () => api.platformRecovery.recoveryLink(identity.userId) });

  function submitRenumber(event: FormEvent) {
    event.preventDefault();
    const normalised = normalisePhone(newPhone);
    setNewPhoneError(normalised ? null : t('auth.phone.required'));
    if (normalised) renumber.mutate(normalised);
  }

  const active = identity.status === 'Active';

  return (
    <Stack>
      <Row>
        <strong>{identity.fullName ?? '—'}</strong>
        <span dir="ltr">{formatPhone(identity.phone)}</span>
        <Badge tone={active ? 'success' : 'neutral'}>{t(`accounts.status.${identity.status}`)}</Badge>
        <Badge tone="info">{t(`accounts.tier.${identity.tier}`)}</Badge>
      </Row>
      <p className="dg-card__hint">{t('accounts.lastSeen', { date: formatDateTime(identity.lastSeenAt) })}</p>
      <ul className="dg-stack dg-stack--tight">
        {identity.memberships.map((m) => (
          <li key={m.membershipId}>
            {m.tenantNameAr ?? t('accounts.platform')} · {m.roleKey} · {t(`status.${m.status}`)}
          </li>
        ))}
      </ul>

      {active && (
        <>
          <form onSubmit={submitRenumber} noValidate>
            <Stack>
              <h3>{t('accounts.renumber.title')}</h3>
              {renumber.isError && <ErrorState error={renumber.error} />}
              {renumber.isSuccess && (
                <Notice tone="success">
                  {t('accounts.renumber.sent', {
                    phone: formatPhone(renumber.data.maskedNewPhone), date: formatDateTime(renumber.data.expiresAt),
                  })}
                </Notice>
              )}
              <Field label={t('accounts.renumber.phone')} error={newPhoneError} required>
                {(props) => <PhoneInput {...props} value={newPhone} onChange={(event) => setNewPhone(event.target.value)} />}
              </Field>
              <Row><Button type="submit" variant="secondary" busy={renumber.isPending}>{t('accounts.renumber.submit')}</Button></Row>
            </Stack>
          </form>

          <Stack>
            <h3>{t('accounts.link.title')}</h3>
            <p className="dg-card__hint">{t('accounts.link.hint')}</p>
            {link.isError && <ErrorState error={link.error} />}
            {link.isSuccess && (
              <Notice tone="warning">
                <Stack tight>
                  <span>{t('accounts.link.shown', { date: formatDateTime(link.data.expiresAt) })}</span>
                  <code dir="ltr" style={{ wordBreak: 'break-all' }}>{link.data.link}</code>
                </Stack>
              </Notice>
            )}
            <Row><Button variant="secondary" busy={link.isPending} onClick={() => link.mutate()}>{t('accounts.link.submit')}</Button></Row>
          </Stack>

          <Stack>
            <h3>{t('accounts.release.title')}</h3>
            <p className="dg-card__hint">{t('accounts.release.hint')}</p>
            {release.isError && <ErrorState error={release.error} />}
            {release.isSuccess
              ? <Notice tone="success">{t('accounts.release.done')}</Notice>
              : (
                <>
                  <label className="dg-checkbox">
                    <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
                    <span>{t('accounts.release.confirm')}</span>
                  </label>
                  <Row>
                    <Button variant="danger" disabled={!confirmed} busy={release.isPending} onClick={() => release.mutate()}>
                      {t('accounts.release.submit')}
                    </Button>
                  </Row>
                </>
              )}
          </Stack>
        </>
      )}
    </Stack>
  );
}
